/* Mystic Modesty — Stories lightbox
   Click thumb → overlay rendered into <body> so it sits above everything
   regardless of ancestor transforms / containment.

   AUDIT CHANGES (2026-07-07):
   - Focus trap added: Tab/Shift+Tab cycle inside the dialog (close button,
     prev/next chevrons, video controls) instead of escaping into the page.
     Disabled/hidden controls are excluded; if a focused chevron becomes
     disabled at a list end, focus moves to the close button.
   - Re-open race fixed: the 240ms "clear lightbox after fade" timer is
     cancelled when a new story opens within that window.
   - Scroll lock now saves/restores the body's previous inline overflow value.
   - Dialog gets aria-label "Story viewer".

   FEATURE (2026-07-07, requested): in-viewer story browsing.
   - At open, the ordered story list is collected from the SECTION containing
     the tapped thumb ([data-mm-stories] ancestor, thumbs in DOM order);
     multiple mm-stories sections on one page never cross-contaminate.
     Placeholder thumbs (no image/video) are skipped.
   - Touch: axis-locked swipe (|dx| > |dy|, lock at 10px, commit at 60px)
     with drag-follow on the lightbox; vertical gestures never navigate.
     No wrap-around: drags past the first/last story are damped (rubber-band)
     and spring back.
   - Keyboard: ArrowLeft/ArrowRight navigate (suppressed while a <video>
     itself has focus, so native seek keys keep working). Chevron buttons are
     real <button>s inside the trap; hidden when only one story; on fine
     pointers they fade in on overlay hover/focus (CSS).
   - IG-style segmented progress bar (one segment per story, filled up to the
     current one) + visually-hidden aria-live "Story X of Y" announcements.
   - Videos: outgoing story's video is paused and reset (currentTime 0)
     before swap; incoming media is rebuilt fresh (same autoplay/controls/
     loop behavior as before) with a defensive play() catch.
   - Rapid-swipe safe: navigation state (index, progress, chrome, live text)
     updates synchronously; only the ~150ms cosmetic slide is deferred, and
     in-flight animation timers are cancelled on every navigate/open/close
     (extends the original close-timer race fix). Adjacent stories are
     preloaded on idle so swaps never blank-flash.
   - DECISION: closing always returns focus to the ORIGINALLY tapped thumb,
     not the last-viewed story's thumb (simpler and predictable). The thumb
     element is captured directly at open — more reliable than
     document.activeElement, which iOS Safari doesn't set on button taps.
   - prefers-reduced-motion: media swaps instantly, no slide/rubber-band.
*/
(function () {
  'use strict';

  // Single shared overlay + controls, appended to <body> on first use.
  var overlay = null;
  var lightbox = null;
  var closeBtn = null;
  var prevBtn = null;
  var nextBtn = null;
  var progressEl = null;
  var liveEl = null;

  var lastFocus = null;
  var clearTimer = null;   // post-close fade cleanup
  var muteBtn = null;
  var ICON_SOUND_ON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z" fill="currentColor" stroke="none"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.2 6a9 9 0 0 1 0 12"/></svg>';
  var ICON_SOUND_OFF = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z" fill="currentColor" stroke="none"/><line x1="16" y1="9" x2="22" y2="15"/><line x1="22" y1="9" x2="16" y2="15"/></svg>';
  function syncMuteBtn(v) {
    if (!muteBtn) return;
    if (!v) { muteBtn.hidden = true; return; }
    muteBtn.hidden = false;
    muteBtn.innerHTML = v.muted ? ICON_SOUND_OFF : ICON_SOUND_ON;
    muteBtn.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
  }
  var navTimer = null;     // in-flight slide / rubber-band animation
  var prevBodyOverflow = '';

  var stories = [];        // [{ image, video, alt }] for the open section
  var current = -1;
  var preloaded = {};      // url → true

  var touchState = { active: false, locked: false, startX: 0, startY: 0, dx: 0 };

  function isOpen() {
    return !!overlay && overlay.classList.contains('mm-stories-overlay--visible');
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function getFocusables() {
    var nodes = overlay.querySelectorAll(
      'button, video[controls], a[href], [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return !el.hidden && !el.disabled;
    });
  }

  function onDocKeydown(e) {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      closeOverlay();
      return;
    }

    if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && stories.length > 1) {
      // Leave arrows alone while the video element itself is focused —
      // native controls use them for seeking.
      var active = document.activeElement;
      if (active && active.tagName === 'VIDEO') return;
      e.preventDefault();
      navigate(e.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (e.key === 'Tab') {
      var focusables = getFocusables();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (!overlay.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- touch: axis-locked swipe with drag-follow ---------- */

  function onTouchStart(e) {
    if (!isOpen() || e.touches.length !== 1) return;
    touchState.active = true;
    touchState.locked = false;
    touchState.dx = 0;
    touchState.startX = e.touches[0].clientX;
    touchState.startY = e.touches[0].clientY;
  }

  function onTouchMove(e) {
    if (!touchState.active) return;
    var dx = e.touches[0].clientX - touchState.startX;
    var dy = e.touches[0].clientY - touchState.startY;

    if (!touchState.locked) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        touchState.locked = true; // horizontal gesture — ours
      } else if (Math.abs(dy) > 10) {
        touchState.active = false; // vertical gesture — never navigate
        return;
      } else {
        return; // undecided yet
      }
    }

    e.preventDefault(); // we own this gesture (listener registered passive:false)

    // Rubber-band damping when dragging past either end (no wrap-around)
    var atStart = current <= 0 && dx > 0;
    var atEnd = current >= stories.length - 1 && dx < 0;
    touchState.dx = (atStart || atEnd || stories.length < 2) ? dx * 0.3 : dx;

    lightbox.classList.remove('is-animating');
    lightbox.style.transform = 'translateX(' + touchState.dx + 'px)';
  }

  function onTouchEnd() {
    if (!touchState.active) return;
    touchState.active = false;
    if (!touchState.locked) return;

    var dx = touchState.dx;
    touchState.dx = 0;
    var goingNext = dx < 0;
    var canGo = stories.length > 1 &&
      (goingNext ? current < stories.length - 1 : current > 0);

    if (Math.abs(dx) > 60 && canGo) {
      navigate(goingNext ? 1 : -1);
    } else {
      // Spring back (also covers the damped rubber-band drag at the ends)
      cancelNavAnim();
      lightbox.classList.add('is-animating');
      lightbox.style.transform = '';
      navTimer = setTimeout(function () {
        navTimer = null;
        lightbox.classList.remove('is-animating');
      }, 200);
    }
  }

  /* ---------- overlay construction ---------- */

  function ensureOverlay() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'mm-stories-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-label', 'Story viewer');

    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'mm-stories-overlay__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    overlay.appendChild(closeBtn);

    muteBtn = document.createElement('button');
    muteBtn.type = 'button';
    muteBtn.className = 'mm-stories-overlay__mute';
    muteBtn.setAttribute('aria-label', 'Mute');
    muteBtn.hidden = true;
    muteBtn.addEventListener('click', function () {
      var v = lightbox ? lightbox.querySelector('video') : null;
      if (!v) return;
      v.muted = !v.muted;
      if (v.muted) v.setAttribute('muted', ''); else v.removeAttribute('muted');
      syncMuteBtn(v);
      if (!v.muted && v.paused) { try { var p = v.play(); if (p && p.catch) p.catch(function(){}); } catch (e) {} }
    });
    overlay.appendChild(muteBtn);

    progressEl = document.createElement('div');
    progressEl.className = 'mm-stories-overlay__progress';
    progressEl.setAttribute('aria-hidden', 'true'); // the live region carries semantics
    progressEl.hidden = true;
    overlay.appendChild(progressEl);

    prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'mm-stories-overlay__nav mm-stories-overlay__nav--prev';
    prevBtn.setAttribute('aria-label', 'Previous story');
    prevBtn.textContent = '‹';
    prevBtn.hidden = true;
    overlay.appendChild(prevBtn);

    nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'mm-stories-overlay__nav mm-stories-overlay__nav--next';
    nextBtn.setAttribute('aria-label', 'Next story');
    nextBtn.textContent = '›';
    nextBtn.hidden = true;
    overlay.appendChild(nextBtn);

    lightbox = document.createElement('div');
    lightbox.className = 'mm-stories-overlay__lightbox';
    overlay.appendChild(lightbox);

    liveEl = document.createElement('div');
    liveEl.className = 'mm-stories-overlay__sr';
    liveEl.setAttribute('aria-live', 'polite');
    overlay.appendChild(liveEl);

    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (e) {
      // Close when clicking ANYWHERE behind the image (i.e. not the image/video itself).
      if (e.target === overlay || e.target === lightbox) closeOverlay();
    });
    closeBtn.addEventListener('click', closeOverlay);
    prevBtn.addEventListener('click', function () { navigate(-1); });
    nextBtn.addEventListener('click', function () { navigate(1); });

    overlay.addEventListener('touchstart', onTouchStart, { passive: true });
    overlay.addEventListener('touchmove', onTouchMove, { passive: false });
    overlay.addEventListener('touchend', onTouchEnd, { passive: true });
    overlay.addEventListener('touchcancel', onTouchEnd, { passive: true });

    // Single document-level listener; overlay is created once and reused, so
    // this is bound exactly once for the page lifetime.
    document.addEventListener('keydown', onDocKeydown);
  }

  /* ---------- media + chrome ---------- */

  function buildMedia(story) {
    var el;
    if (story.video) {
      el = document.createElement('video');
      el.src = story.video;
      el.autoplay = true;
      el.loop = true;
      // No native controls (2026-07-13, Ebrahim): iOS renders its heavy
      // QuickTime chrome (dimming, skip buttons, scrubber) over the video —
      // clashes with the seamless story feel. IG pattern instead: tap the
      // video to pause/resume, custom mute button in the overlay chrome.
      el.addEventListener('click', function () {
        try { if (el.paused) { var p = el.play(); if (p && p.catch) p.catch(function(){}); } else { el.pause(); } } catch (e) {}
      });
      // iOS fix 2026-07-13: WebKit requires the ATTRIBUTES (property alone can
      // still trigger native fullscreen/QuickTime).
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
      // Sound ON by default (2026-07-13, Ebrahim): the tap that opened the
      // viewer is the user gesture, so unmuted playback is permitted — the
      // IG/TikTok pattern. If a device still refuses, swapMedia falls back
      // to muted playback instead of a frozen frame.
    } else if (story.image) {
      el = document.createElement('img');
      el.src = story.image;
      el.alt = story.alt || '';
    } else {
      return null;
    }
    return el;
  }

  function pauseOutgoingVideo() {
    var v = lightbox.querySelector('video');
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch (e) {}
    }
  }

  function swapMedia(story) {
    lightbox.innerHTML = '';
    var el = buildMedia(story);
    if (!el) return;
    lightbox.appendChild(el);
    if (el.tagName === 'VIDEO' && typeof el.play === 'function') {
      syncMuteBtn(el);
      try {
        var p = el.play();
        if (p && p.catch) p.catch(function () {
          // Autoplay-with-sound refused on this device -> degrade to muted
          // playback (shopper unmutes via our button) rather than freezing.
          try {
            el.muted = true;
            el.setAttribute('muted', '');
            syncMuteBtn(el);
            var p2 = el.play();
            if (p2 && p2.catch) p2.catch(function () {});
          } catch (e2) {}
        });
      } catch (e) {}
    } else {
      syncMuteBtn(null);
    }
  }

  function updateChrome() {
    var n = stories.length;
    var multi = n > 1;

    if (progressEl.children.length !== n) {
      progressEl.innerHTML = '';
      for (var i = 0; i < n; i++) {
        var seg = document.createElement('span');
        seg.className = 'mm-stories-overlay__progress-seg';
        progressEl.appendChild(seg);
      }
    }
    for (var j = 0; j < n; j++) {
      progressEl.children[j].classList.toggle('is-filled', j <= current);
      progressEl.children[j].classList.toggle('is-current', j === current);
    }

    progressEl.hidden = !multi;
    prevBtn.hidden = !multi;
    nextBtn.hidden = !multi;
    prevBtn.disabled = current <= 0;
    nextBtn.disabled = current >= n - 1;

    // Don't strand keyboard focus on a control that just became disabled
    if (prevBtn.disabled && document.activeElement === prevBtn) closeBtn.focus();
    if (nextBtn.disabled && document.activeElement === nextBtn) closeBtn.focus();

    liveEl.textContent = 'Story ' + (current + 1) + ' of ' + n;
  }

  function setStory(index) {
    current = index;
    updateChrome();
    pauseOutgoingVideo();
    swapMedia(stories[current]);
  }

  /* ---------- navigation ---------- */

  function cancelNavAnim() {
    if (navTimer) {
      clearTimeout(navTimer);
      navTimer = null;
    }
  }

  function resetLightboxStyles() {
    lightbox.classList.remove('is-animating');
    lightbox.style.transform = '';
    lightbox.style.opacity = '';
  }

  function navigate(dir) {
    if (stories.length < 2) return;
    var next = current + dir;
    if (next < 0 || next > stories.length - 1) {
      rubberBand(dir);
      return;
    }

    // State updates synchronously (rapid-swipe safe); only the slide is async.
    current = next;
    updateChrome();
    var story = stories[current];
    pauseOutgoingVideo();
    cancelNavAnim();

    if (reducedMotion()) {
      resetLightboxStyles();
      swapMedia(story);
      schedulePreload();
      return;
    }

    // Slide the outgoing media toward the swipe direction (continues smoothly
    // from any drag-follow offset), swap, then slide the incoming media in.
    lightbox.classList.add('is-animating');
    lightbox.style.transform = 'translateX(' + (dir * -64) + 'px)';
    lightbox.style.opacity = '0';
    navTimer = setTimeout(function () {
      navTimer = null;
      swapMedia(story);
      lightbox.classList.remove('is-animating');
      lightbox.style.transform = 'translateX(' + (dir * 64) + 'px)';
      void lightbox.offsetWidth; // reflow so the return transition runs
      lightbox.classList.add('is-animating');
      lightbox.style.transform = '';
      lightbox.style.opacity = '';
      navTimer = setTimeout(function () {
        navTimer = null;
        lightbox.classList.remove('is-animating');
      }, 180);
      schedulePreload();
    }, 150);
  }

  function rubberBand(dir) {
    if (reducedMotion()) return;
    cancelNavAnim();
    lightbox.classList.add('is-animating');
    lightbox.style.transform = 'translateX(' + (dir * -22) + 'px)';
    navTimer = setTimeout(function () {
      lightbox.style.transform = '';
      navTimer = setTimeout(function () {
        navTimer = null;
        lightbox.classList.remove('is-animating');
      }, 180);
    }, 110);
  }

  /* ---------- adjacent preload (no blank flashes) ---------- */

  function preloadStory(i) {
    if (i < 0 || i >= stories.length) return;
    var s = stories[i];
    var key = s.video || s.image;
    if (!key || preloaded[key]) return;
    preloaded[key] = true;
    if (s.video) {
      var v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.src = s.video;
    } else {
      var im = new Image();
      im.src = s.image;
    }
  }

  function schedulePreload() {
    var run = function () {
      preloadStory(current - 1);
      preloadStory(current + 1);
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 200);
    }
  }

  /* ---------- open / close ---------- */

  function openFromThumb(thumb) {
    // Story list = every media-carrying thumb of the SAME section, DOM order.
    var section = thumb.closest ? thumb.closest('[data-mm-stories]') : null;
    var thumbs = section ? section.querySelectorAll('[data-mm-stories-open]') : [thumb];
    var list = [];
    var idx = -1;
    for (var i = 0; i < thumbs.length; i++) {
      var t = thumbs[i];
      var s = {
        image: t.getAttribute('data-mm-stories-image') || '',
        video: t.getAttribute('data-mm-stories-video') || '',
        alt: t.getAttribute('data-mm-stories-alt') || ''
      };
      if (!s.image && !s.video) continue; // placeholder slot — skip
      if (t === thumb) idx = list.length;
      list.push(s);
    }
    if (idx === -1) return; // tapped thumb has no media (matches old early return)

    ensureOverlay();
    // Cancel a pending "clear after close-fade" timer + any in-flight slide so
    // a quick re-open doesn't get its media wiped or inherit stale transforms.
    if (clearTimer) {
      clearTimeout(clearTimer);
      clearTimer = null;
    }
    cancelNavAnim();
    resetLightboxStyles();

    stories = list;
    lastFocus = thumb; // focus always returns to the ORIGINALLY tapped thumb
    setStory(idx);

    overlay.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      overlay.classList.add('mm-stories-overlay--visible');
    });
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.focus();
    schedulePreload();
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.classList.remove('mm-stories-overlay--visible');
    overlay.setAttribute('aria-hidden', 'true');
    touchState.active = false;
    cancelNavAnim();
    resetLightboxStyles();
    pauseOutgoingVideo();
    clearTimer = setTimeout(function () {
      lightbox.innerHTML = '';
      clearTimer = null;
    }, 240);
    document.body.style.overflow = prevBodyOverflow;
    prevBodyOverflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  }

  /* ---------- thumb binding ---------- */

  function bindThumbs(scope) {
    var root = scope || document;
    var thumbs = root.querySelectorAll('[data-mm-stories-open]');
    Array.prototype.forEach.call(thumbs, function (thumb) {
      if (thumb.__mmStoriesBound) return;
      thumb.__mmStoriesBound = true;
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        openFromThumb(thumb);
      });
    });
  }

  function init() {
    bindThumbs(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when sections are reloaded in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    bindThumbs(e.target);
  });
})();
